import { Injectable } from '@angular/core';
import {AuthService, AuthServiceProvider} from './auth.service';
import {HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import { environment } from '../../../environments/environment';
import {catchError, shareReplay, switchMap} from "rxjs/operators";

/*
 Service responsible for attaching proper Authorization Headers to api requests
 Jwt interceptor recognises services which should be authorized by CASA token
*/
@Injectable()
export class JwtInterceptorService  implements HttpInterceptor {
  private apiEndpoint = environment.apiEndpoint;
  private authServerEndpoint = environment.authEndpoint;
  private casaEndpoint = environment.casaEndpoint;
  constructor(private authService: AuthServiceProvider) { }

  filter(req: HttpRequest<any>): boolean {
    if (req.url.startsWith(this.apiEndpoint)) { return true; }
    if (req.url.startsWith(this.authServerEndpoint)) { return true; }
    if (req.url.startsWith(environment.videoApiBase)) {return true; }
    if (req.url.startsWith(environment.transApiBase)) {return true; }
    if (req.url.startsWith(environment.eventsApiBase)) {return true; }
    return req.url.startsWith(this.casaEndpoint);
  }

  applyHeaders(auth: AuthService, next: HttpHandler, req: HttpRequest<any>): Observable<HttpEvent<any>> {
    if (!auth.isTokenValid() || !auth.getAccessToken()) {
      auth.clear();
      return next.handle(req);
    }
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const headers = { Authorization: `Bearer ${auth.getAccessToken()}`, Timezone: timezone};
    return next.handle(req.clone({setHeaders: headers}))
  }

  lastRefreshed = 0;
  lastRefreshShare: Observable<any>;
  tryRefreshToken(auth: AuthService, _: HttpHandler): Observable<any> {
    if (Date.now() - this.lastRefreshed < 2000) return this.lastRefreshShare;
    this.lastRefreshed = Date.now();
    this.lastRefreshShare = auth.refreshToken().pipe(shareReplay(1));
    return this.lastRefreshShare;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler):
    Observable<HttpEvent<any>> {
    if (!this.filter(req) || this.isUnprotected(req)) {
      return next.handle(req);
    }

    return this.authService.get().pipe(
      switchMap( api =>
        this.applyHeaders(api, next, req).pipe(
          catchError(error => {
            // handle 401 responses only  - requires a refresh token
            if (!(error instanceof HttpErrorResponse) || error.status != 401) return throwError(error);
            return this.tryRefreshToken(api, next).pipe(
              switchMap( _ => this.applyHeaders(api, next, req))
            )
          }),
          catchError( error => {
            if (!(error instanceof HttpErrorResponse) || error.status != 401) return throwError(error);
            api.logout();
            return throwError(error);
          })
        )
      )
    )

  }

  private isUnprotected(req: HttpRequest<any>) {
    return  (req.url.startsWith(this.authServerEndpoint) &&
        (req.url.endsWith('/authorize') || req.url.endsWith("/token"))
      );
  }
}
